import {
  atom,
  getDefaultStore,
  type Getter,
  type Setter,
  type WritableAtom,
} from 'jotai';
import isEqual from 'lodash/isEqual';

const DEFAULT_DEBOUNCE_MS = 1000;

type AutoSaveFormConfig<TForm extends Record<string, unknown>, TServerData> = {
  defaultForm: TForm;
  fromServerData: (data: TServerData) => TForm;
  createMutation: (
    onSuccess: (get: Getter, set: Setter, ticketId: string) => void,
    onError: (get: Getter, set: Setter, ticketId: string) => void,
  ) => WritableAtom<null, [{ ticketId: string; form: TForm }], void>;
  refetchOnSuccess: (get: Getter, ticketId: string) => void;
  debounceMs?: number;
};

export type SyncFormResult = {
  overwrittenFields: string[];
};

export function createAutoSaveFormAtoms<
  TForm extends Record<string, unknown>,
  TServerData,
>(config: AutoSaveFormConfig<TForm, TServerData>) {
  const debounceMs = config.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  // 🔵🔵🔵 LOCAL STATE - Form data keyed by ticket ID
  const $formById = atom<Map<string, TForm>>(new Map());
  // 🔵🔵🔵 LOCAL STATE - Last saved server snapshot (used to detect unsaved changes)
  const $baselineById = atom<Map<string, TForm | null>>(new Map());
  // 🔵🔵🔵 LOCAL STATE - Timestamp of last successful server sync per ticket
  const $lastSyncedById = atom<Map<string, string | null>>(new Map());
  // 🔵🔵🔵 LOCAL STATE - Pending debounce timers keyed by ticket ID
  const $debounceTimers = atom<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  // 🔵🔵🔵 LOCAL STATE - Entity version (updatedAt) per ticket
  const $versionById = atom<Map<string, string>>(new Map());
  // 🔵🔵🔵 LOCAL STATE - Version expected after our own save (to detect self-originated changes)
  const $pendingSaveVersionById = atom<Map<string, boolean>>(new Map());

  const onSuccess = (get: Getter, set: Setter, ticketId: string) => {
    const currentForm = get($formById).get(ticketId) ?? config.defaultForm;
    set($baselineById, (prev) => {
      const m = new Map(prev);
      m.set(ticketId, currentForm);
      return m;
    });
    set($pendingSaveVersionById, (prev) => {
      const m = new Map(prev);
      m.set(ticketId, true);
      return m;
    });
    config.refetchOnSuccess(get, ticketId);
  };

  const onError = (get: Getter, set: Setter, ticketId: string) => {
    const baseline = get($baselineById).get(ticketId) ?? null;
    if (baseline) {
      set($formById, (prev) => {
        const m = new Map(prev);
        m.set(ticketId, baseline);
        return m;
      });
    }
  };

  // 🟠🟠🟠 ACTION - Save form to server
  const $saveAtom = config.createMutation(onSuccess, onError);

  // 🟠🟠🟠 ACTION - Update a single text field with debounced auto-save
  const $updateField = atom(
    null,
    (
      get,
      set,
      params: { field: keyof TForm; value: string; ticketId: string },
    ) => {
      const current = get($formById).get(params.ticketId) ?? config.defaultForm;
      set($formById, (prev) => {
        const m = new Map(prev);
        m.set(params.ticketId, {
          ...current,
          [params.field]: params.value || null,
        });
        return m;
      });

      const timers = get($debounceTimers);
      const prevTimer = timers.get(params.ticketId);
      if (prevTimer) clearTimeout(prevTimer);

      const ticketId = params.ticketId;
      const newTimer = setTimeout(() => {
        const store = getDefaultStore();
        const currentForm =
          store.get($formById).get(ticketId) ?? config.defaultForm;
        const baseline = store.get($baselineById).get(ticketId) ?? null;
        if (isEqual(currentForm, baseline)) return;
        store.set($saveAtom, { ticketId, form: currentForm });
      }, debounceMs);

      set($debounceTimers, (prev) => {
        const newMap = new Map(prev);
        newMap.set(params.ticketId, newTimer);
        return newMap;
      });
    },
  );

  // 🟠🟠🟠 ACTION - Sync server data into local form state with three-way merge
  const $syncForm = atom(
    null,
    (
      get,
      set,
      params: {
        ticketId: string;
        serverUpdatedAt?: string;
        ticket: TServerData;
      },
    ): SyncFormResult => {
      const result: SyncFormResult = { overwrittenFields: [] };

      const timers = get($debounceTimers);
      const prevTimer = timers.get(params.ticketId);
      if (prevTimer) clearTimeout(prevTimer);
      set($debounceTimers, (prev) => {
        const newMap = new Map(prev);
        newMap.delete(params.ticketId);
        return newMap;
      });

      if (params.serverUpdatedAt) {
        const currentVersion = get($versionById).get(params.ticketId) ?? null;
        if (currentVersion && params.serverUpdatedAt <= currentVersion) {
          return result;
        }
      }

      const isSelfOriginated =
        get($pendingSaveVersionById).get(params.ticketId) === true;

      const { serverUpdatedAt } = params;
      if (serverUpdatedAt) {
        set($versionById, (prev) => {
          const m = new Map(prev);
          m.set(params.ticketId, serverUpdatedAt);
          return m;
        });
        set($lastSyncedById, (prev) => {
          const m = new Map(prev);
          m.set(params.ticketId, serverUpdatedAt);
          return m;
        });
      }

      if (isSelfOriginated) {
        set($pendingSaveVersionById, (prev) => {
          const m = new Map(prev);
          m.delete(params.ticketId);
          return m;
        });
      }

      const snapshot = config.fromServerData(params.ticket);

      const currentBaseline = get($baselineById).get(params.ticketId) ?? null;
      const currentForm =
        get($formById).get(params.ticketId) ?? config.defaultForm;

      if (currentBaseline === null || isEqual(currentForm, currentBaseline)) {
        set($formById, (prev) => {
          const m = new Map(prev);
          m.set(params.ticketId, snapshot);
          return m;
        });
        set($baselineById, (prev) => {
          const m = new Map(prev);
          m.set(params.ticketId, snapshot);
          return m;
        });
        return result;
      }

      const mergedForm = { ...currentForm };
      const overwrittenFields: string[] = [];

      for (const key of Object.keys(snapshot) as (keyof TForm & string)[]) {
        const serverValue = snapshot[key];
        const baselineValue = currentBaseline[key];
        const localValue = currentForm[key];

        const serverChanged = !isEqual(serverValue, baselineValue);
        const localChanged = !isEqual(localValue, baselineValue);

        if (serverChanged) {
          mergedForm[key] = serverValue;
          if (localChanged && !isSelfOriginated) {
            overwrittenFields.push(key);
          }
        }
      }

      set($formById, (prev) => {
        const m = new Map(prev);
        m.set(params.ticketId, mergedForm);
        return m;
      });
      set($baselineById, (prev) => {
        const m = new Map(prev);
        m.set(params.ticketId, snapshot);
        return m;
      });

      result.overwrittenFields = overwrittenFields;
      return result;
    },
  );

  // 🟠🟠🟠 ACTION - Cancel pending debounce timer without saving
  const $cancelAutoSave = atom(null, (get, set, ticketId: string) => {
    const timers = get($debounceTimers);
    const timer = timers.get(ticketId);
    if (timer) {
      clearTimeout(timer);
      set($debounceTimers, (prev) => {
        const newMap = new Map(prev);
        newMap.delete(ticketId);
        return newMap;
      });
    }
  });

  // 🟠🟠🟠 ACTION - Immediately save pending changes (cancels debounce and saves now)
  const $flushAutoSave = atom(null, (get, set, ticketId: string) => {
    const timers = get($debounceTimers);
    const timer = timers.get(ticketId);
    if (timer) {
      clearTimeout(timer);
      set($debounceTimers, (prev) => {
        const newMap = new Map(prev);
        newMap.delete(ticketId);
        return newMap;
      });
    }

    const currentForm = get($formById).get(ticketId) ?? config.defaultForm;
    const baseline = get($baselineById).get(ticketId) ?? null;
    if (isEqual(currentForm, baseline)) return;

    set($saveAtom, { ticketId, form: currentForm });
  });

  // 🟠🟠🟠 ACTION - Update fields and save immediately (bypasses debounce, for select inputs)
  const $updateImmediately = atom(
    null,
    (get, set, params: { ticketId: string; updates: Partial<TForm> }) => {
      const current = get($formById).get(params.ticketId) ?? config.defaultForm;
      set($formById, (prev) => {
        const m = new Map(prev);
        m.set(params.ticketId, { ...current, ...params.updates });
        return m;
      });

      set($cancelAutoSave, params.ticketId);

      const store = getDefaultStore();
      const currentForm =
        store.get($formById).get(params.ticketId) ?? config.defaultForm;
      store.set($saveAtom, { ticketId: params.ticketId, form: currentForm });
    },
  );

  const $cleanupForm = atom(null, (get, set, ticketId: string) => {
    const timers = get($debounceTimers);
    const timer = timers.get(ticketId);
    if (timer) clearTimeout(timer);

    set($formById, (prev) => {
      const m = new Map(prev);
      m.delete(ticketId);
      return m;
    });
    set($baselineById, (prev) => {
      const m = new Map(prev);
      m.delete(ticketId);
      return m;
    });
    set($lastSyncedById, (prev) => {
      const m = new Map(prev);
      m.delete(ticketId);
      return m;
    });
    set($debounceTimers, (prev) => {
      const m = new Map(prev);
      m.delete(ticketId);
      return m;
    });
    set($versionById, (prev) => {
      const m = new Map(prev);
      m.delete(ticketId);
      return m;
    });
    set($pendingSaveVersionById, (prev) => {
      const m = new Map(prev);
      m.delete(ticketId);
      return m;
    });
  });

  return {
    $formById,
    $baselineById,
    $versionById,
    $updateField,
    $updateImmediately,
    $syncForm,
    $flushAutoSave,
    $cancelAutoSave,
    $cleanupForm,
  };
}
